import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { APIProvider } from "@vis.gl/react-google-maps";
import clsx from "clsx";
import { Control, get, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  placeholder?: string;
  countryCode?: string[];
  handleStreetInput: (e: any) => void;
  initValue?: string;
  disabled?: boolean;
}

export const PlaceAutocomplete = ({
  onPlaceSelect,
  placeholder,
  countryCode,
  handleStreetInput,
  initValue = "",
  disabled = false,
}: PlaceAutocompleteProps) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");
  const [inputValue, setInputValue] = useState<string | null>(initValue);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["name", "formatted_address", "address_components"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    // Reset keyState when initValue changes (e.g., when country changes)
    setInputValue(initValue);
  }, [initValue]);

  useEffect(() => {
    if (countryCode)
      placeAutocomplete?.setComponentRestrictions({
        country: countryCode,
      });
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener("place_changed", () => {
      const place = placeAutocomplete.getPlace();
      onPlaceSelect(place);
      if (place.name) {
        setInputValue(place.name);
      }
    });
  }, [onPlaceSelect, placeAutocomplete, countryCode]);

  const handleChange = (e: any) => {
    handleStreetInput(e.target.value);
    setInputValue(e.target.value);
  };

  return (
    <div
      className={clsx(
        "relative flex h-14 w-full items-center rounded-lg border px-4 hover:border-gray-300",
        {
          "hover:cursor-not-allowed": disabled,
        },
        disabled ? "bg-gray-100" : "bg-white",
      )}
    >
      <input
        ref={inputRef}
        className={clsx(
          "peer flex h-[80%] w-full bg-transparent focus:outline-hidden",
          {
            "translate-y-1.5": inputValue && inputValue.length > 0,
          },
          disabled && "hover:cursor-not-allowed",
        )}
        placeholder="" // Keep this empty to hide the default placeholder
        value={inputValue || ""}
        onChange={(e) => handleChange(e)}
        disabled={disabled}
      />
      {/* {inputMemo} */}
      <label
        className={clsx(
          "pointer-events-none absolute flex transform truncate overflow-hidden text-sm text-gray-500 transition-all duration-200 ease-in-out",
          {
            "-translate-y-3 text-xs": inputValue && inputValue.length > 0,
            "text-xs": inputValue && inputValue.length > 0,
          },
        )}
        onClick={() => inputRef.current?.focus()} // Focus input when label is clicked
      >
        {placeholder}
      </label>
    </div>
  );
};

type AutocompleteSearchProps = {
  placeholder?: string;
  onPlaceClicked: (place: google.maps.places.PlaceResult | null) => void;
  countryCode?: string[];
  control: Control<any>;
  handleStreetInput: (e: any) => void;
  formValueOverride?: string;
  disabled?: boolean;
};

export const AutocompleteSearch = ({
  placeholder,
  onPlaceClicked,
  countryCode,
  control,
  handleStreetInput,
  formValueOverride,
  disabled = false,
}: AutocompleteSearchProps) => {
  const { t } = useTranslation("boExpeditions");
  const translateMessage = useTranslateMessage();
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);

  const { errors } = useFormState({ control });
  const errorMessage = get(
    errors,
    formValueOverride ? `${formValueOverride}.message` : "street.message",
  ) as unknown as string;
  const streetWatch: string = useWatch({
    control,
    name: formValueOverride || "street",
  });

  useEffect(() => {
    if (!selectedPlace) return;
    onPlaceClicked(selectedPlace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlace]);

  return (
    <APIProvider
      apiKey={"AIzaSyDIEG5sck092TlO8WTxyEQjawmwtPMf3vw"}
      solutionChannel="GMP_devsite_samples_v3_rgmautocomplete"
    >
      <PlaceAutocomplete
        onPlaceSelect={setSelectedPlace}
        placeholder={placeholder || t("pendingShipmentsAddressSearch.streetPlaceholder")}
        countryCode={countryCode}
        handleStreetInput={handleStreetInput}
        initValue={streetWatch}
        disabled={disabled}
      />
      {errorMessage && <p className="text-xs text-red-500">{translateMessage(errorMessage)}</p>}
    </APIProvider>
  );
};
